import { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Combobox,
  Drawer,
  Group,
  InputBase,
  List,
  ScrollArea,
  Stack,
  Text,
  Title,
  useCombobox,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconArrowRight } from '@tabler/icons-react';
import { useMergeParties, useParty, usePartiesList } from '../../queries/parties';
import { ApiError } from '../../lib/api-error';
import type { Party, PartyDetail } from '../../types/api';
import { buildMergePreview } from './merge-preview';

interface Props {
  opened: boolean;
  onClose: () => void;
  /** The party that will SURVIVE the merge. */
  survivor: Party;
  /** Optional callback after a successful merge. */
  onMerged?: (newSurvivor: Party) => void;
}

/**
 * Pick another party in the same project to absorb into `survivor`. Shows a
 * preview of what will move so the user understands the side effects before
 * confirming. The resulting alias/attachment dedup is handled server-side.
 */
export function MergePartyDrawer({ opened, onClose, survivor, onMerged }: Props) {
  const [absorbId, setAbsorbId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  // Use the same list endpoint as the main page; cap to 50 hits and filter
  // out the survivor + any soft-deleted rows.
  const { data: candidates = [] } = usePartiesList({
    q: search.trim() || undefined,
    limit: 50,
  });
  const visible = useMemo(
    () => candidates.filter((p) => p.id !== survivor.id),
    [candidates, survivor.id],
  );

  const survivorDetailQuery = useParty(survivor.id);
  const absorbDetailQuery = useParty(absorbId);
  const merge = useMergeParties();

  const survivorDetail: PartyDetail | undefined = survivorDetailQuery.data;
  const absorbDetail: PartyDetail | undefined = absorbDetailQuery.data;

  const preview = useMemo(
    () => buildMergePreview(survivorDetail, absorbDetail),
    [survivorDetail, absorbDetail],
  );

  function close() {
    setAbsorbId(null);
    setSearch('');
    onClose();
  }

  async function confirmMerge() {
    if (!absorbId) return;
    try {
      const res = await merge.mutateAsync({ survivor_id: survivor.id, absorb_id: absorbId });
      notifications.show({
        title: 'Merged',
        message: `${res.stats.absorbed_party_name} → ${res.party.canonical_name} (${res.stats.aliases_moved} aliases, ${res.stats.attachments_moved} attachments)`,
        color: 'pine',
      });
      onMerged?.(res.party);
      close();
    } catch (err) {
      notifications.show({
        title: 'Merge failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  return (
    <Drawer
      opened={opened}
      onClose={close}
      position="right"
      size="lg"
      padding="md"
      title={<Title order={4}>Merge into {survivor.canonical_name}</Title>}
      scrollAreaComponent={ScrollArea.Autosize}
      overlayProps={{ backgroundOpacity: 0.15 }}
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Pick the duplicate party. Its aliases and runsheet attachments move to{' '}
          <Text span fw={600}>
            {survivor.canonical_name}
          </Text>
          . The duplicate is then soft-deleted. This cannot be undone in-app.
        </Text>

        <Combobox
          store={combobox}
          withinPortal
          onOptionSubmit={(val) => {
            setAbsorbId(val);
            const match = visible.find((p) => p.id === val);
            setSearch(match?.canonical_name ?? '');
            combobox.closeDropdown();
          }}
        >
          <Combobox.Target>
            <InputBase
              label="Duplicate party to absorb"
              rightSection={<Combobox.Chevron />}
              rightSectionPointerEvents="none"
              value={search}
              onChange={(e) => {
                setSearch(e.currentTarget.value);
                setAbsorbId(null);
                combobox.openDropdown();
              }}
              onFocus={() => combobox.openDropdown()}
              placeholder="Search by name or alias"
            />
          </Combobox.Target>
          <Combobox.Dropdown>
            <Combobox.Options mah={260} style={{ overflowY: 'auto' }}>
              {visible.length === 0 ? (
                <Combobox.Empty>No matches</Combobox.Empty>
              ) : (
                visible.map((p) => (
                  <Combobox.Option key={p.id} value={p.id}>
                    <Group justify="space-between">
                      <Text size="sm">{p.canonical_name}</Text>
                      <Group gap="xs">
                        <Badge size="xs" variant="light">
                          {p.party_type}
                        </Badge>
                        {p.mention_count > 0 && (
                          <Badge size="xs" variant="light" color="blue">
                            {p.mention_count} mention{p.mention_count === 1 ? '' : 's'}
                          </Badge>
                        )}
                      </Group>
                    </Group>
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Combobox.Dropdown>
        </Combobox>

        {absorbDetail && preview && (
          <Stack gap="xs">
            <Group gap="xs" align="center">
              <Text fw={600}>{absorbDetail.party.canonical_name}</Text>
              <IconArrowRight size={16} />
              <Text fw={600}>{survivor.canonical_name}</Text>
            </Group>

            <List size="sm" spacing={4}>
              {preview.addCanonicalAsAlias && (
                <List.Item>
                  <Text size="sm">
                    "{absorbDetail.party.canonical_name}" will be added as an alias of{' '}
                    {survivor.canonical_name}
                  </Text>
                </List.Item>
              )}
              {preview.newAliases.length > 0 ? (
                <List.Item>
                  <Text size="sm">
                    {preview.newAliases.length} new alias
                    {preview.newAliases.length === 1 ? '' : 'es'} will move:
                  </Text>
                  <Group gap={4} mt={4}>
                    {preview.newAliases.slice(0, 8).map((a) => (
                      <Badge key={a} size="xs" variant="light">
                        {a}
                      </Badge>
                    ))}
                    {preview.newAliases.length > 8 && (
                      <Badge size="xs" variant="light" color="gray">
                        +{preview.newAliases.length - 8} more
                      </Badge>
                    )}
                  </Group>
                </List.Item>
              ) : (
                <List.Item>
                  <Text size="sm" c="dimmed">
                    No new aliases (everything is already a known alias of the survivor)
                  </Text>
                </List.Item>
              )}
              <List.Item>
                <Text size="sm">
                  All runsheet entry attachments on{' '}
                  <Text span fw={500}>
                    {absorbDetail.party.canonical_name}
                  </Text>{' '}
                  will be re-pointed. Duplicate (entry, role) pairs are silently dropped.
                </Text>
              </List.Item>
            </List>

            <Alert
              icon={<IconAlertTriangle size={14} />}
              color="yellow"
              variant="light"
              radius="md"
              p="sm"
            >
              <Text size="xs">
                The duplicate party is soft-deleted but its history stays in the audit log. If you
                merge the wrong pair, the audit table records the change but undo is manual.
              </Text>
            </Alert>
          </Stack>
        )}

        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={close}>
            Cancel
          </Button>
          <Button
            color="red"
            disabled={!absorbId}
            loading={merge.isPending}
            onClick={confirmMerge}
          >
            Merge
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
